import React, { useMemo, useState } from 'react';
import { put, generateId } from '../hooks/useDB';
import {
  GraduationCap, ClipboardList, NotebookPen, FileSignature, CalendarDays, UserCheck,
  CheckCircle2, Clock, Loader2, Send, Play, Download, ShieldCheck, AlertTriangle, Info, Plus,
} from 'lucide-react';
import { toast } from '../utils/toast';
import CommentThread from '../components/onboarding/CommentThread';
import SignaturePad from '../components/onboarding/SignaturePad';
import {
  ONBOARDING_DAYS, JOURNEY_STATUS, TASK_STATUS, ENGINEER_TASK_STATUSES,
  currentDay, fmtDate, fmtDateTime, journeyProgress, today,
} from '../data/onboardingPlan';

export default function OnboardingEmployee({ currentUser = {}, journey, tasks, logs, docs }) {
  const [tab, setTab] = useState('tasks');

  const day = currentDay(journey.startDate);
  const progress = journeyProgress(tasks);
  const visibleDocs = docs.filter((d) => d.published);
  const unsignedCount = visibleDocs.filter((d) => !d.signedAt).length;
  const awaitingReview = tasks.filter((t) => t.status === 'submitted').length;
  const totalHours = logs.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
  const statusMeta = JOURNEY_STATUS[journey.status] || JOURNEY_STATUS.active;

  const TABS = [
    { id: 'tasks', label: 'My Tasks', icon: ClipboardList, count: tasks.length },
    { id: 'logs', label: 'Daily Log', icon: NotebookPen, count: logs.length },
    { id: 'docs', label: 'Documents', icon: FileSignature, count: unsignedCount || visibleDocs.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <GraduationCap style={{ color: 'var(--accent)' }} size={24} />
            <span>My Onboarding</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Your {ONBOARDING_DAYS}-day plan, daily log and documents — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <span className={`sh-badge ${statusMeta.badge}`}>{statusMeta.label}</span>
          {day > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-white shadow-sm" style={{ background: 'var(--accent)' }}>
              <CalendarDays size={14} /> Day {day} of {ONBOARDING_DAYS}
            </span>
          )}
        </div>
      </div>

      {/* Journey summary */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Role</div>
          <div className="font-semibold text-slate-900 mt-0.5">{journey.role || '—'}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Your mentor</div>
          <div className="font-semibold text-slate-900 mt-0.5 flex items-center gap-1.5">
            <UserCheck size={14} style={{ color: 'var(--accent)' }} />
            {journey.mentorName || journey.mentorEmail || 'To be assigned'}
          </div>
          {journey.mentorEmail && <div className="text-xs text-slate-500 truncate">{journey.mentorEmail}</div>}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Plan window</div>
          <div className="font-semibold text-slate-900 mt-0.5 text-sm">
            {fmtDate(journey.startDate)} → {fmtDate(journey.endDate)}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Tasks approved</span>
            <CheckCircle2 className="text-emerald-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{progress.approved}/{progress.total}</div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
            <div className="h-full rounded-full transition-all" style={{ width: `${progress.percent}%`, background: 'var(--accent)' }} />
          </div>
        </div>
        <div className="sh-stat-card border-l-4 border-amber-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Awaiting review</span>
            <Clock className="text-amber-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{awaitingReview}</div>
          <span className="text-xs text-slate-500">Submitted to your mentor</span>
        </div>
        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Work logged</span>
            <NotebookPen className="text-slate-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{logs.length} days</div>
          <span className="text-xs text-slate-500">{totalHours ? `${totalHours} hours total` : 'Log your day below'}</span>
        </div>
      </div>

      {/* Documents ready to sign — surfaced above the tabs so it can't be missed */}
      {unsignedCount > 0 && (
        <button
          type="button"
          onClick={() => setTab('docs')}
          className="w-full text-left bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 hover:bg-amber-100/70 transition"
        >
          <FileSignature className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div>
            <span className="font-semibold text-amber-900 block">
              {unsignedCount} document{unsignedCount === 1 ? '' : 's'} waiting for your signature
            </span>
            <span className="text-sm text-amber-800">Open the Documents tab to review and e-sign.</span>
          </div>
        </button>
      )}

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Icon size={15} /> {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'tasks' && <TasksTab currentUser={currentUser} tasks={tasks} />}
      {tab === 'logs' && <LogsTab currentUser={currentUser} journey={journey} logs={logs} />}
      {tab === 'docs' && <DocsTab currentUser={currentUser} docs={visibleDocs} />}
    </div>
  );
}

// ── Tasks ───────────────────────────────────────────────────────────────────
function TasksTab({ currentUser, tasks }) {
  const sorted = useMemo(() => [...tasks].sort((a, b) => (a.day || 0) - (b.day || 0)), [tasks]);

  if (!sorted.length) {
    return (
      <EmptyCard
        icon={ClipboardList}
        title="No tasks assigned yet"
        body="Your mentor will publish your 15-day plan here. Check back shortly."
      />
    );
  }

  return (
    <div className="space-y-3">
      {sorted.map((task) => <EngineerTaskCard key={task.id} task={task} currentUser={currentUser} />)}
    </div>
  );
}

function EngineerTaskCard({ task, currentUser }) {
  const [note, setNote] = useState(task.progressNote || '');
  const [saving, setSaving] = useState(false);
  const meta = TASK_STATUS[task.status] || TASK_STATUS.todo;
  const locked = task.status === 'approved';
  // Saving a note must send a status the engineer is allowed to set. After the
  // mentor asks for changes, picking the work back up means "in progress".
  const saveStatus = ENGINEER_TASK_STATUSES.includes(task.status) ? task.status : 'in-progress';

  const save = async (nextStatus) => {
    if (saving) return;
    setSaving(true);
    try {
      await put('onboarding_tasks', { ...task, status: nextStatus, progressNote: note });
      toast.success(nextStatus === 'submitted' ? 'Submitted for review.' : 'Progress saved.');
    } catch (e) {
      toast.error(e.message || 'Could not save your progress.');
    } finally {
      setSaving(false);
    }
  };

  const addComment = async (body) => {
    const comment = {
      id: generateId(),
      body,
      authorName: currentUser.name,
      authorEmail: currentUser.email,
      authorRole: 'engineer',
      createdAt: new Date().toISOString(),
    };
    try {
      await put('onboarding_tasks', { ...task, comments: [...(task.comments || []), comment] });
    } catch (e) {
      toast.error(e.message || 'Could not post your comment.');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded" style={{ background: 'var(--accent)' }}>
                Day {task.day}
              </span>
              <span className={`sh-badge ${meta.badge}`}>{meta.label}</span>
            </div>
            <h4 className="font-bold text-slate-900 mt-2">{task.title}</h4>
            {task.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{task.description}</p>}
          </div>
        </div>

        {/* Reviewer's verdict */}
        {task.reviewComment && (
          <div className={`rounded-lg px-3 py-2 text-sm border ${task.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
            <span className="font-semibold block text-xs uppercase tracking-wider mb-0.5">
              {task.status === 'approved' ? 'Approved' : 'Changes requested'}
              {task.reviewedBy ? ` · ${task.reviewedBy}` : ''}
            </span>
            {task.reviewComment}
          </div>
        )}

        {/* Progress note + actions */}
        {!locked ? (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your progress note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="What you did, links to PRs, anything you're stuck on…"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
            />
            <div className="flex flex-wrap gap-2">
              {task.status === 'todo' && (
                <button
                  onClick={() => save('in-progress')}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
                >
                  <Play size={14} /> Start
                </button>
              )}
              <button
                onClick={() => save(saveStatus)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save note
              </button>
              {task.status !== 'submitted' && (
                <button
                  onClick={() => save('submitted')}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50"
                >
                  <Send size={14} /> Submit for review
                </button>
              )}
            </div>
          </div>
        ) : (
          task.progressNote && (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-150 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">Your note</span>
              {task.progressNote}
            </div>
          )
        )}
      </div>

      <div className="border-t border-slate-100 bg-slate-50/40 p-4">
        <CommentThread comments={task.comments} canComment onAdd={addComment} placeholder="Reply to your mentor…" />
      </div>
    </div>
  );
}

// ── Daily log ───────────────────────────────────────────────────────────────
function LogsTab({ currentUser, journey, logs }) {
  const [form, setForm] = useState({ day: String(currentDay(journey.startDate) || 1), date: today(), hours: '', summary: '', blockers: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sorted = useMemo(() => [...logs].sort((a, b) => (b.day || 0) - (a.day || 0)), [logs]);

  const submit = async () => {
    if (!form.summary.trim() || saving) return;
    setSaving(true);
    try {
      await put('onboarding_logs', {
        id: generateId(),
        onboardingId: journey.id,
        engineerEmail: journey.engineerEmail,
        mentorEmail: journey.mentorEmail,
        day: Number(form.day) || null,
        date: form.date,
        hours: form.hours,
        summary: form.summary.trim(),
        blockers: form.blockers.trim(),
        comments: [],
        createdByEmail: currentUser.email,
        createdByName: currentUser.name,
        createdAt: new Date().toISOString(),
      });
      setForm({ day: String(Math.min((Number(form.day) || 1) + 1, ONBOARDING_DAYS)), date: today(), hours: '', summary: '', blockers: '' });
      toast.success('Work log added — your mentor can see it now.');
    } catch (e) {
      toast.error(e.message || 'Could not save your log.');
    } finally {
      setSaving(false);
    }
  };

  const addComment = async (log, body) => {
    const comment = {
      id: generateId(), body,
      authorName: currentUser.name, authorEmail: currentUser.email, authorRole: 'engineer',
      createdAt: new Date().toISOString(),
    };
    try {
      await put('onboarding_logs', { ...log, comments: [...(log.comments || []), comment] });
    } catch (e) {
      toast.error(e.message || 'Could not post your comment.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5 space-y-3">
        <h4 className="font-bold text-slate-900 flex items-center gap-2">
          <Plus size={16} style={{ color: 'var(--accent)' }} /> Log today's work
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Day</label>
            <select
              value={form.day}
              onChange={(e) => set('day', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            >
              {Array.from({ length: ONBOARDING_DAYS }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>Day {d}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => set('date', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Hours</label>
            <input
              type="number" min="0" step="0.5"
              value={form.hours}
              onChange={(e) => set('hours', e.target.value)}
              placeholder="8"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">What did you work on? *</label>
          <textarea
            value={form.summary}
            onChange={(e) => set('summary', e.target.value)}
            rows={3}
            placeholder="Shipped the login bug fix, paired with Aditya on the API…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Blockers or questions</label>
          <textarea
            value={form.blockers}
            onChange={(e) => set('blockers', e.target.value)}
            rows={2}
            placeholder="Anything slowing you down — your mentor sees this."
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none transition resize-none"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={!form.summary.trim() || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} Add log
          </button>
        </div>
      </div>

      {/* History */}
      {sorted.length === 0 ? (
        <EmptyCard icon={NotebookPen} title="No work logged yet" body="Add your first daily log above — it's how your mentor follows your progress." />
      ) : (
        <div className="space-y-3">
          {sorted.map((log) => (
            <div key={log.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 sm:p-5 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded" style={{ background: 'var(--accent)' }}>Day {log.day}</span>
                  <span className="text-xs text-slate-500 font-mono">{fmtDate(log.date)}</span>
                  {log.hours && <span className="text-xs font-semibold text-slate-600">{log.hours}h</span>}
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{log.summary}</p>
                {log.blockers && (
                  <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span className="whitespace-pre-wrap">{log.blockers}</span>
                  </div>
                )}
              </div>
              <div className="border-t border-slate-100 bg-slate-50/40 p-4">
                <CommentThread comments={log.comments} canComment onAdd={(body) => addComment(log, body)} placeholder="Reply…" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────
function DocsTab({ currentUser, docs }) {
  if (!docs.length) {
    return (
      <EmptyCard
        icon={FileSignature}
        title="No documents shared yet"
        body="Your contract documents appear here once your onboarding is confirmed. You'll be able to read and e-sign them."
      />
    );
  }
  return (
    <div className="space-y-3">
      {docs.map((doc) => <SignDocCard key={doc.id} doc={doc} currentUser={currentUser} />)}
    </div>
  );
}

function SignDocCard({ doc, currentUser }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentUser.name || '');
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const signed = Boolean(doc.signedAt);

  const sign = async () => {
    if (saving) return;
    if (!name.trim()) { toast.error('Type your full name to sign.'); return; }
    if (!consent) { toast.error('Please confirm the consent checkbox.'); return; }
    if (!signature) { toast.error('Please draw your signature.'); return; }
    setSaving(true);
    try {
      await put('onboarding_docs', {
        ...doc,
        signatureName: name.trim(),
        signatureImage: signature,
        consent: true,
        signerUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      toast.success('Signed — thank you! A copy stays available here.');
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Could not record your signature.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-slate-900 truncate">{doc.name}</h4>
            {signed
              ? <span className="sh-badge sh-badge-success">Signed</span>
              : <span className="sh-badge sh-badge-warning">Signature required</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {signed
              ? `Signed by ${doc.signatureName} on ${fmtDateTime(doc.signedAt)}`
              : `Shared ${fmtDateTime(doc.publishedAt)}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {doc.dataUrl && (
            <a
              href={doc.dataUrl}
              download={doc.name}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
            >
              <Download size={14} /> Read
            </a>
          )}
          {!signed && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition"
            >
              <FileSignature size={14} /> {open ? 'Cancel' : 'Sign'}
            </button>
          )}
        </div>
      </div>

      {signed && doc.signatureImage && (
        <div className="border-t border-slate-100 bg-emerald-50/40 p-4 flex items-center gap-4">
          <img src={doc.signatureImage} alt={`Signature of ${doc.signatureName}`} className="h-16 bg-white border border-slate-200 rounded-lg" />
          <div className="text-xs text-slate-600">
            <div className="flex items-center gap-1.5 font-semibold text-emerald-700">
              <ShieldCheck size={14} /> Electronically signed
            </div>
            <div className="mt-0.5">{doc.signatureName}</div>
            <div className="font-mono">{fmtDateTime(doc.signedAt)}</div>
          </div>
        </div>
      )}

      {open && !signed && (
        <div className="border-t border-slate-100 p-4 sm:p-5 space-y-4 bg-slate-50/40">
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
            <Info size={14} className="shrink-0 mt-0.5 text-slate-400" />
            <span>Please read the document before signing. Your typed name, drawn signature and the date and time are recorded together as your electronic signature.</span>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Full legal name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="As it appears on your ID"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-slate-800 outline-none transition"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Draw your signature *</label>
            <SignaturePad onChange={setSignature} />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
            <span>I have read and agree to this document, and I consent to signing it electronically.</span>
          </label>
          <div className="flex justify-end">
            <button
              onClick={sign}
              disabled={saving || !name.trim() || !consent || !signature}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Sign document
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
      <Icon className="mx-auto text-slate-300 mb-3" size={36} />
      <h3 className="text-base font-bold text-slate-800 font-display">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{body}</p>
    </div>
  );
}
